import React, { useState, useEffect, useMemo } from "react";
import { Modal, Group, TextInput, Button, Table, Text, Menu, ActionIcon, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { DocumentData } from "firebase/firestore";
import MetadataOptionsEditor from "./metadata-options-editor";
import { Braces, Edit, LayoutNavbarCollapse, Plus, Trash, GripVertical, Copy, Qrcode, DotsVertical, Search, X } from "tabler-icons-react";
import { FlexFormSelectOption, OptionsMetadata } from "@captiva/shared-types";
import QRCodeLib from "qrcode";
import { saveAs } from "file-saver";

interface FormSelectOptionsModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (docData: { name: string; options: FlexFormSelectOption[] }) => void;
  initialDoc: DocumentData | null;
}

export default function FormSelectOptionsModal({
  opened,
  onClose,
  onSave,
  initialDoc
}: FormSelectOptionsModalProps) {
  const [name, setName] = useState(initialDoc?.name || "");
  const [options, setOptions] = useState<FlexFormSelectOption[]>(initialDoc?.options || []);
  const [optionSearchTerm, setOptionSearchTerm] = useState("");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [pendingMetadata, setPendingMetadata] = useState<Record<string, OptionsMetadata>>({});
  const [showMetaForm, setShowMetaForm] = useState(false);
  // For editing metadata of existing options
  const [editingMetaIdx, setEditingMetaIdx] = useState<number | null>(null);
  const [editingMeta, setEditingMeta] = useState<Record<string, OptionsMetadata>>({});
  // For editing option label/value
  const [editingOptionIdx, setEditingOptionIdx] = useState<number | null>(null);
  const [editOptionLabel, setEditOptionLabel] = useState("");
  const [editOptionValue, setEditOptionValue] = useState("");
  // For new option metadata
  const [metaKey, setMetaKey] = useState("");
  const [metaLabel, setMetaLabel] = useState("");
  const [metaValue, setMetaValue] = useState("");
  // For editing metadata of existing options
  const [editMetaKey, setEditMetaKey] = useState("");
  const [editMetaLabel, setEditMetaLabel] = useState("");
  const [editMetaValue, setEditMetaValue] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    setName(initialDoc?.name || "");
    setOptions(initialDoc?.options || []);
    setOptionSearchTerm("");
    setEditingMetaIdx(null);
    setEditingMeta({});
    setEditingOptionIdx(null);
    setEditOptionLabel("");
    setEditOptionValue("");
  }, [initialDoc]);

  // Edit option label/value
  const startEditOption = (idx: number) => {
    setEditingOptionIdx(idx);
    setEditOptionLabel(options[idx].label || "");
    setEditOptionValue(options[idx].value || "");
  };

  const saveEditOption = () => {
    if (editingOptionIdx === null) return;
    const newOptions = [...options];
    newOptions[editingOptionIdx] = {
      ...newOptions[editingOptionIdx],
      label: editOptionLabel,
      value: editOptionValue,
    };
    setOptions(newOptions);
    setEditingOptionIdx(null);
    setEditOptionLabel("");
    setEditOptionValue("");
  };

  const addOption = () => {
    if (!label || !value) return;
    const newOption: FlexFormSelectOption = {
      label,
      value,
    };
    if (Object.keys(pendingMetadata).length > 0) {
      newOption.metadata = pendingMetadata;
    }
    setOptions([...options, newOption]);
    setLabel("");
    setValue("");
    setPendingMetadata({});
  };
  const removeOption = (idx: number) => setOptions(options.filter((_, i) => i !== idx));

  const duplicateOption = (idx: number) => {
    const optionToDuplicate = options[idx];
    if (!optionToDuplicate) return;

    const duplicatedOption: FlexFormSelectOption = {
      ...optionToDuplicate,
      label: `${optionToDuplicate.label || ""} copy`.trim(),
      value: `${optionToDuplicate.value || ""}_copy`.replace(/^_copy$/, "copy"),
    };
    if (optionToDuplicate.metadata) {
      duplicatedOption.metadata = JSON.parse(JSON.stringify(optionToDuplicate.metadata));
    } else {
      delete duplicatedOption.metadata;
    }

    const duplicatedIdx = idx + 1;
    const newOptions = [...options];
    newOptions.splice(duplicatedIdx, 0, duplicatedOption);
    setOptions(newOptions);

    // Open the duplicated row directly in edit mode so users can quickly adjust label/value.
    setEditingOptionIdx(duplicatedIdx);
    setEditOptionLabel(duplicatedOption.label);
    setEditOptionValue(duplicatedOption.value);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || optionSearchTerm.trim()) return;
    const reordered = Array.from(options);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setOptions(reordered);
  };

  const filteredOptionsWithIndices = useMemo(() => {
    return options
      .map((opt, originalIndex) => ({ opt, originalIndex }))
      .filter(({ opt }) => {
        if (!optionSearchTerm.trim()) return true;
        const term = optionSearchTerm.toLowerCase();
        const labelMatches = (opt.label || "").toLowerCase().includes(term);
        const valueMatches = String(opt.value ?? "").toLowerCase().includes(term);
        return labelMatches || valueMatches;
      });
  }, [options, optionSearchTerm]);

  const startEditMeta = (idx: number) => {
    setEditingMetaIdx(idx);
    setEditingMeta((options[idx].metadata as Record<string, OptionsMetadata>) || {});
    setEditMetaKey("");
    setEditMetaLabel("");
    setEditMetaValue("");
  };

  const saveEditMeta = () => {
    if (editingMetaIdx !== null) {
      setOptions(prevOptions => {
        const updated = [...prevOptions];
        const updatedOpt = { ...updated[editingMetaIdx] };
        if (Object.keys(editingMeta).length > 0) {
          updatedOpt.metadata = { ...editingMeta };
        } else {
          delete updatedOpt.metadata;
        }
        updated[editingMetaIdx] = updatedOpt;
        return updated;
      });
    }
    setEditingMetaIdx(null);
    setEditingMeta({});
  };

  const addMetaToEdit = () => {
    if (!editMetaKey || !editMetaLabel) return;
    setEditingMeta({ ...editingMeta, [editMetaKey]: { label: editMetaLabel, value: editMetaValue } });
    setEditMetaKey("");
    setEditMetaLabel("");
    setEditMetaValue("");
  };

  const removeMetaFromEdit = (key: string) => {
    const newMeta = { ...editingMeta };
    delete newMeta[key];
    setEditingMeta(newMeta);
  };

  const updateMetaFromEdit = (oldKey: string, field: "key" | "label" | "value", nextValue: string) => {
    if (!oldKey || !field) return;

    if (field === "key") {
      const trimmedKey = (nextValue || "").trim();
      if (!trimmedKey || trimmedKey === oldKey) return;
      if (Object.prototype.hasOwnProperty.call(editingMeta, trimmedKey)) return;

      const existing = editingMeta[oldKey] || { label: "", value: "" };
      const updatedMeta = { ...editingMeta };
      delete updatedMeta[oldKey];
      updatedMeta[trimmedKey] = existing;
      setEditingMeta(updatedMeta);
      return;
    }

    if (field === "label" || field === "value") {
      setEditingMeta((prev) => ({
        ...prev,
        [oldKey]: {
          ...(prev[oldKey] || { label: "", value: "" }),
          [field]: nextValue,
        },
      }));
    }
  };

  // Metadata for the option being created
  const addMeta = () => {
    if (!metaKey || !metaLabel) return;
    setPendingMetadata({ ...pendingMetadata, [metaKey]: { label: metaLabel, value: metaValue } });
    setMetaKey("");
    setMetaLabel("");
    setMetaValue("");
  };
  const removeMeta = (key: string) => {
    const newMeta = { ...pendingMetadata };
    delete newMeta[key];
    setPendingMetadata(newMeta);
  };

  const handleSave = () => {
    const sanitizedOptions = options.map((opt) => {
      const sanitizedOpt: FlexFormSelectOption = {
        label: opt.label ?? "",
        value: opt.value ?? "",
      };
      if (opt.metadata && Object.keys(opt.metadata).length > 0) {
        const cleanedMetadata: Record<string, OptionsMetadata> = {};
        Object.entries(opt.metadata).forEach(([key, meta]) => {
          if (meta) {
            cleanedMetadata[key] = {
              label: meta.label ?? "",
              value: meta.value === undefined ? "" : meta.value,
            };
          }
        });
        sanitizedOpt.metadata = cleanedMetadata;
      }
      return sanitizedOpt;
    });
    onSave({ ...initialDoc, name, options: sanitizedOptions });
  };

  const styles = {
    td: { maxWidth: "100px" },
  };

  return (
    <Modal
      size="xl"
      opened={opened}
      onClose={onClose}
      title={initialDoc ? "Edit options set" : "New options set"}
      {...({ fullScreen: isMobile } as any)}
    >
      <Group mb="xs" align="flex-end" style={{ width: "100%", flexDirection: isMobile ? "column" : "row" }}>
        <TextInput
          size={isMobile ? "md" : "sm"}
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          style={{ width: "100%", flex: 1 }}
        />
        <TextInput
          size={isMobile ? "md" : "sm"}
          type="search"
          placeholder="Search options by label..."
          label="Filter Options"
          icon={<Search size={16} />}
          value={optionSearchTerm}
          onChange={(e) => setOptionSearchTerm(e.currentTarget.value)}
          rightSection={
            optionSearchTerm ? (
              <ActionIcon size="xs" onClick={() => setOptionSearchTerm("")} title="Clear search">
                <X size={14} />
              </ActionIcon>
            ) : null
          }
          style={{ width: "100%", flex: 1 }}
        />
      </Group>
      {optionSearchTerm.trim() && (
        <Text size="xs" color="dimmed" mb="xs">
          Showing {filteredOptionsWithIndices.length} of {options.length} options (reordering disabled while filtering)
        </Text>
      )}
      <div style={{ maxHeight: isMobile ? undefined : 400, overflowY: isMobile ? 'visible' : 'auto' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Table>
            <thead>
              <tr>
                <th></th>
                <th>Label</th>
                <th>Value</th>
                <th style={{ textAlign: "right", paddingRight: 40 }}>Actions</th>
              </tr>
            </thead>
            <Droppable droppableId="options">
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {filteredOptionsWithIndices.map(({ opt, originalIndex }, displayIdx) => (
                    <Draggable
                      key={originalIndex}
                      draggableId={`option-${originalIndex}`}
                      index={displayIdx}
                      isDragDisabled={Boolean(optionSearchTerm.trim())}
                    >
                      {(provided) => (
                        <tr ref={provided.innerRef} {...provided.draggableProps}>
                          <td
                            {...provided.dragHandleProps}
                            style={{
                              cursor: optionSearchTerm.trim() ? 'not-allowed' : 'grab',
                              textAlign: 'center',
                              padding: '8px',
                              opacity: optionSearchTerm.trim() ? 0.3 : 1,
                            }}
                          >
                            <GripVertical size={18} />
                          </td>
                          <td style={styles.td}>{opt.label}</td>
                          <td style={styles.td}>{String(opt.value ?? "")}</td>
                          <td>
                            <Group spacing={isMobile ? "md" : "xs"} position="right" noWrap>
                              <Tooltip label="Edit" withArrow position="top">
                                <ActionIcon size={isMobile ? "xl" : "lg"} color="cap-blue" variant="outline" onClick={() => startEditOption(originalIndex)} aria-label="Edit" style={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}>
                                  <Edit size={isMobile ? 20 : 18} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Delete" withArrow position="top">
                                <ActionIcon size={isMobile ? "xl" : "lg"} color="red" variant="outline" onClick={() => removeOption(originalIndex)} aria-label="Delete" style={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}>
                                  <Trash size={isMobile ? 20 : 18} />
                                </ActionIcon>
                              </Tooltip>
                              <Menu
                                size="sm"
                                position="bottom"
                                placement="end"
                               control={
                                  <ActionIcon size={isMobile ? "xl" : "lg"} variant="outline" color="gray" aria-label="More options" style={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}>
                                    <DotsVertical size={isMobile ? 20 : 18} />
                                  </ActionIcon>
                                }
                              >
                                <Menu.Item icon={<Braces size={16} />} onClick={() => startEditMeta(originalIndex)}>
                                  Metadata ({opt.metadata ? Object.keys(opt.metadata).length : 0})
                                </Menu.Item>
                                <Menu.Item icon={<Copy size={16} />} onClick={() => duplicateOption(originalIndex)}>
                                  Copy
                                </Menu.Item>
                                <Menu.Item icon={<Qrcode size={16} />} onClick={() => generateAndDownloadSingleQRCode(opt, name)}>
                                  Download QR
                                </Menu.Item>
                              </Menu>
                            </Group>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {filteredOptionsWithIndices.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", padding: "24px", color: "#888" }}>
                        No options match "{optionSearchTerm}"
                      </td>
                    </tr>
                  )}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </div>
      {editingOptionIdx !== null && (
        <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, marginTop: 16 }}>
          <Text weight={500} mb={8}>Editing option: "{options[editingOptionIdx]?.label}"</Text>
          <Group>
            <TextInput label="Label" value={editOptionLabel} onChange={e => setEditOptionLabel(e.target.value)} required />
            <TextInput label="Value" value={editOptionValue} onChange={e => setEditOptionValue(e.target.value)} required />
          </Group>
          <Group position="right" mt="xs">
            <Button variant="default" color="gray" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={() => { setEditingOptionIdx(null); setEditOptionLabel(""); setEditOptionValue(""); }}>Cancel</Button>
            <Button color="cap-navy" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={saveEditOption} disabled={!editOptionLabel || !editOptionValue}>Save</Button>
          </Group>
        </div>
      )}
      <Modal
        opened={editingMetaIdx !== null}
        onClose={() => { setEditingMetaIdx(null); setEditingMeta({}); }}
        title={editingMetaIdx !== null ? `Editing metadata for option: "${options[editingMetaIdx]?.label}"` : ''}
        size="lg"
        overlayOpacity={0.55}
        overlayBlur={3}
        centered
      >
        {editingMetaIdx !== null && (
          <div>
            <MetadataOptionsEditor
              metadata={editingMeta}
              onAdd={addMetaToEdit}
              onRemove={removeMetaFromEdit}
              onUpdate={updateMetaFromEdit}
              metaKey={editMetaKey}
              setMetaKey={setEditMetaKey}
              metaLabel={editMetaLabel}
              setMetaLabel={setEditMetaLabel}
              metaValue={editMetaValue}
              setMetaValue={setEditMetaValue}
              addDisabled={!editMetaKey || !editMetaLabel || !editMetaValue}
            />
            <Group position="right" mt="xs">
              <Button variant="default" color="gray" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={() => { setEditingMetaIdx(null); setEditingMeta({}); }}>Cancel</Button>
              <Button color="cap-navy" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : 36 }} onClick={saveEditMeta}>Save Metadata</Button>
            </Group>
          </div>
        )}
      </Modal>
      <Group mb="xs" style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, margin: '16px 0', flexDirection: 'column', alignItems: 'stretch' }}>
        <Text size="md" weight={500}>New Option</Text>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 8, width: '100%' }}>
          <TextInput size={isMobile ? "md" : "sm"} label="Label" value={label} onChange={(e) => setLabel(e.target.value)} style={{ flex: 1 }} />
          <TextInput size={isMobile ? "md" : "sm"} label="Value" value={value} onChange={(e) => setValue(e.target.value)} style={{ flex: 1 }} />
          <Button
            size={isMobile ? "md" : "sm"}
            color="cap-blue"
            variant="filled"
            leftIcon={<Plus size={16} />}
            onClick={addOption}
            style={{ alignSelf: isMobile ? 'stretch' : 'end', minHeight: isMobile ? 44 : undefined, marginTop: isMobile ? 8 : 0 }}
          >
            Add option
          </Button>
        </div>
        {!showMetaForm && (
          <Button
            size={isMobile ? "md" : "sm"}
            color="cap-blue"
            variant="outline"
            onClick={() => setShowMetaForm(true)}
            style={{ alignSelf: isMobile ? 'stretch' : 'flex-start', minHeight: isMobile ? 44 : undefined, marginTop: 4 }}
            leftIcon={<Braces size={16} />}
          >
            Configure metadata
          </Button>
        )}
        {showMetaForm && (
          <div style={{ background: '#f9f9f9', padding: 8, borderRadius: 8, width: "100%" }}>
            <Button
              size={isMobile ? "md" : "sm"}
              color="cap-blue"
              variant="outline"
              onClick={() => setShowMetaForm(false)}
              style={{ alignSelf: isMobile ? 'stretch' : 'flex-start', marginBottom: 8, minHeight: isMobile ? 44 : undefined }}
              leftIcon={<LayoutNavbarCollapse size={16} />}
            >
              Hide metadata
            </Button>
            {Object.keys(pendingMetadata).length > 0 && (
              <Table striped highlightOnHover style={{ border: '1px solid #e0e0e0', borderRadius: 8, margin: "8px 0" }}>
                <thead>
                  <tr><th>Meta Key</th><th>Label</th><th>Value</th><th></th></tr>
                </thead>
                <tbody>
                  {Object.entries(pendingMetadata).map(([key, meta]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{meta.label}</td>
                      <td>{String(meta.value ?? "")}</td>
                      <td>
                        <Button
                          size={isMobile ? "md" : "xs"}
                          color="red"
                          variant="outline"
                          onClick={() => removeMeta(key)}
                          px={6}
                          style={{ minWidth: isMobile ? 44 : 0, minHeight: isMobile ? 44 : undefined }}
                          aria-label="Remove metadata"
                        >
                          <Trash size={isMobile ? 18 : 16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Group mt="xs" style={{ display: 'flex', flexDirection: 'row' }}>
              <TextInput size="xs" style={{ width: '25%' }} label="Meta Key" value={metaKey} onChange={(e) => setMetaKey(e.target.value)} error={!metaKey && metaKey !== '' ? 'Required' : undefined} />
              <TextInput size="xs" style={{ width: '25%' }} label="Meta Label" value={metaLabel} onChange={(e) => setMetaLabel(e.target.value)} error={!metaLabel && metaLabel !== '' ? 'Required' : undefined} />
              <TextInput size="xs" style={{ width: '25%' }} label="Meta Value" value={metaValue} onChange={(e) => setMetaValue(e.target.value)} error={!metaValue && metaValue !== '' ? 'Required' : undefined} />
              <Button
                color="cap-blue"
                style={{ alignSelf: 'flex-end', minHeight: isMobile ? 44 : 36 }}
                variant="outline"
                size={isMobile ? "md" : "sm"}
                leftIcon={<Plus size={16} />}
                onClick={addMeta}
                disabled={!metaKey || !metaLabel || !metaValue}
              >
                Add
              </Button>
            </Group>
          </div>
        )}
      </Group>
      <Group position="right" mt="md">
        <Button variant="default" color="gray" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : undefined }} onClick={onClose}>
          Cancel
        </Button>
        <Button color="cap-navy" size={isMobile ? "md" : "sm"} style={{ minHeight: isMobile ? 44 : undefined }} disabled={!name} onClick={handleSave}>
          {initialDoc ? "Save" : "Create"}
        </Button>
      </Group>
    </Modal>
  );
}

async function generateAndDownloadSingleQRCode(option: FlexFormSelectOption, setName: string) {
  if (!option) return;
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const qrValue = `${option.label}:${option.value}`;
  await QRCodeLib.toCanvas(canvas, qrValue, {
    width: 400,
    margin: 10,
    errorCorrectionLevel: 'H'
  });
  const logoImg = new window.Image();
  logoImg.src = '/captiva-mark.png';
  await new Promise((resolve) => {
    logoImg.onload = resolve;
    logoImg.onerror = resolve;
  });
  const logoX = 20;
  const logoY = 20;
  const logoSize = 40;
  ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
  ctx.font = "bold 16px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  ctx.fillStyle = "#333";
  ctx.textBaseline = "middle";
  ctx.fillText(`${setName || ""} - ${option.label}`, logoX + logoSize + 16, logoY + logoSize / 2);
  canvas.toBlob((blob) => {
    if (blob) saveAs(blob, `${option.label}-${option.value}-qr.png`);
  }, "image/png");
}
